import type { ReactNode } from 'react';
import type { AuthState } from './GlobalShell.js';
import { RepoListSkeleton } from './RepoListSkeleton.js';
import { RepoListEmpty } from './RepoListEmpty.js';
import { RepoListError } from './RepoListError.js';
import { SignedOutOverlay } from './SignedOutOverlay.js';
import { RepoRow } from './RepoRow.js';

interface RepoSummary {
  fullName: string;
  org: string;
  repo: string;
  indexedAt?: string;
  registeredAt?: string;
  nodeCount?: number;
}

interface RepoListProps {
  authState: AuthState;
  repos: RepoSummary[];
  loading: boolean;
  error: boolean;
  starredRepos: Set<string>;
  lastSearchCounts: Record<string, number>;
  reindexState: Record<string, 'idle' | 'loading' | 'error'>;
  onStar: (fullName: string, e: React.MouseEvent) => void;
  onReindex: (fullName: string, org: string, repo: string, e: React.MouseEvent) => void;
  onRetry: () => void;
}

/**
 * S-UI-002: Dense row list replacing the old card grid.
 *
 * States:
 *   loading     → skeleton rows
 *   signed-out  → skeleton rows dimmed with overlay
 *   error       → error message + Retry
 *   empty       → empty-state copy
 *   populated   → RepoRow list (starred-first, then alphabetical)
 */
export function RepoList({
  authState,
  repos,
  loading,
  error,
  starredRepos,
  lastSearchCounts,
  reindexState,
  onStar,
  onReindex,
  onRetry,
}: RepoListProps): ReactNode {
  const isSignedIn = authState === 'signed-in';
  const isSignedOut = authState === 'signed-out';

  // Signed-out state: show dimmed skeleton with overlay
  if (isSignedOut) {
    return (
      <div
        data-testid="repo-list"
        className="repo-list repo-list--signed-out"
        aria-label="Repositories"
      >
        <h2 className="repo-list__heading">Repositories</h2>
        <div className="repo-list__signed-out-container">
          <RepoListSkeleton />
          <SignedOutOverlay />
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div data-testid="repo-list" className="repo-list" aria-label="Repositories">
        <h2 className="repo-list__heading">Repositories</h2>
        <RepoListSkeleton />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div data-testid="repo-list" className="repo-list" aria-label="Repositories">
        <h2 className="repo-list__heading">Repositories</h2>
        <RepoListError onRetry={onRetry} />
      </div>
    );
  }

  // Empty state
  if (repos.length === 0) {
    return (
      <div data-testid="repo-list" className="repo-list" aria-label="Repositories">
        <h2 className="repo-list__heading">Repositories</h2>
        <RepoListEmpty isSignedIn={isSignedIn} />
      </div>
    );
  }

  // Populated — starred-first, then alphabetical within each group
  const sortedRepos = [...repos].sort((a, b) => {
    const aStarred = starredRepos.has(a.fullName);
    const bStarred = starredRepos.has(b.fullName);
    if (aStarred !== bStarred) return aStarred ? -1 : 1;
    return a.fullName.localeCompare(b.fullName);
  });

  return (
    <div
      data-testid="repo-list"
      className="repo-list"
      role="list"
      aria-label={`Repositories (${repos.length})`}
    >
      <h2 className="repo-list__heading">
        Repositories <span className="repo-list__count">({repos.length})</span>
      </h2>
      {sortedRepos.map(r => (
        <RepoRow
          key={r.fullName}
          repo={r}
          isStarred={starredRepos.has(r.fullName)}
          lastSearchCount={lastSearchCounts[r.fullName]}
          onStar={(e) => onStar(r.fullName, e)}
          onReindex={(e) => onReindex(r.fullName, r.org, r.repo, e)}
          reindexing={reindexState[r.fullName] === 'loading'}
          reindexFailed={reindexState[r.fullName] === 'error'}
        />
      ))}
    </div>
  );
}
